import { type Lens } from '@dhmk/zustand-lens';

import { getMergedCatalogFieldsForSelection } from '../models/issues/catalog-field-merge';
import type { DynamicFieldValues, Field } from '../models/issues/field';
import {
  type Area,
  type Attachment,
  type Category,
  type Department,
} from '../models/issues/issue';
import type { SchemaErrors } from '../models/issues/validation';
import {
  validateCustomField,
  validateDraftIssueWithZod,
  validateField,
} from '../models/issues/validation';
import { generateSubmissionId } from '../utils/utils';
import { getIssueFields } from './create-issue-fields';
import { resolveDraftIssueSelections } from './draft-issue-selection';

export type EscalationData = {
  reason?: string;
  referenceNumber?: string;
  hasConfirmedContact?: boolean;
};

type State = {
  submissionId?: string;
  description?: string;
  attachments?: Attachment[];
  areaKey?: string;
  departmentKey?: string;
  categoryKey?: string;
  fields?: string[];
  errors?: SchemaErrors;
  customFields?: DynamicFieldValues;
  escalationData?: EscalationData;
  hasAcknowledgedNudge?: boolean;
};

type Actions = {
  ensureSubmissionId: () => string;
  setDescription: (description: string) => void;
  setArea: (area: Area) => void;
  setDepartment: (department: Department) => void;
  setCategory: (category: Category) => void;
  addAttachment: (attachment: Attachment) => void;
  setAttachments: (attachments: Attachment[]) => void;
  removeAttachment: (attachment: Attachment) => void;
  removeAttachments: () => void;
  setCustomField: (fieldId: string, value: string | string[]) => void;
  setHasAcknowledgedNudge: (hasAcknowledgedNudge: boolean) => void;
  setEscalationData: (data: Partial<EscalationData>) => void;
  clearEscalationData: () => void;
  reset: () => void;
  clearSummaryFields: () => void;
  // TODO: rename
  validateAll: () => { isValid: boolean; errors?: SchemaErrors };
};

export type DraftIssueSlice = State & Actions;

const SUMMARY_ERROR_KEYS = new Set<keyof SchemaErrors>([
  'description',
  'customFields',
]);

// Minimal type to read catalog fields from the full store state via api.getState().
// We cannot import the full Store type from store-provider.ts as it would create a
// circular import (store-provider imports DraftIssueStore). The cast is safe because
// api is the full Zustand store API, and we only access the well-known catalog slice.
type StoreWithCatalog = {
  catalog?: {
    catalog?: { categories?: Area[]; fields?: Record<string, Field> };
  };
};

const getCatalogFields = (api: {
  getState: () => unknown;
}): Record<string, Field> =>
  (api.getState() as StoreWithCatalog).catalog?.catalog?.fields ?? {};

const getCatalogAreas = (api: { getState: () => unknown }): Area[] =>
  (api.getState() as StoreWithCatalog).catalog?.catalog?.categories ?? [];

/**
 * Field metadata as the colleague currently sees it: the catalog defaults with
 * the selected category's (or department's) overrides applied. Validating
 * against the base catalog instead would reject option values the selection
 * legitimately offers.
 */
const getSelectionFields = (
  draftIssue: State,
  api: { getState: () => unknown }
): Record<string, Field> => {
  const { department, category } = resolveDraftIssueSelections(
    draftIssue,
    getCatalogAreas(api)
  );

  return getMergedCatalogFieldsForSelection(
    getCatalogFields(api),
    department,
    category
  );
};

/**
 * Values are deliberately carried across a categorisation change so the
 * colleague does not retype them. The one value that cannot be carried is a
 * selection the new configuration no longer offers: the select renders it as
 * nothing at all, so the colleague can neither see it nor correct it. Every
 * other kind of invalid value stays put, because the field shows it back to
 * them along with an error they can act on.
 */
const pruneUnselectableValue = (
  value: string | string[] | undefined,
  field: Field | undefined
): string | string[] | undefined => {
  const allowedOptionIds = field?.options?.map(({ id }) => id) ?? [];

  if (field?.inputType !== 'select' || allowedOptionIds.length === 0) {
    return value;
  }

  if (Array.isArray(value)) {
    const retained = value.filter((entry) => allowedOptionIds.includes(entry));
    return retained.length > 0 ? retained : undefined;
  }

  if (!value) {
    return value;
  }

  return allowedOptionIds.includes(value) ? value : undefined;
};

const reconcileCustomFields = (
  draftIssue: State,
  activeFieldIds: string[],
  fields: Record<string, Field>
): Pick<State, 'customFields' | 'errors'> => {
  const { customFields, errors } = draftIssue;

  if (!customFields) {
    return { customFields, errors };
  }

  const reconciledEntries = Object.entries(customFields).map(
    ([fieldId, value]) =>
      [
        fieldId,
        activeFieldIds.includes(fieldId)
          ? pruneUnselectableValue(value, fields[fieldId])
          : value,
      ] as const
  );

  const prunedFieldIds = reconciledEntries
    .filter(([fieldId, value]) => value !== customFields[fieldId])
    .map(([fieldId]) => fieldId);

  if (prunedFieldIds.length === 0) {
    return { customFields, errors };
  }

  const retainedEntries = reconciledEntries.filter(
    ([, value]) => value !== undefined
  );
  const retainedCustomFieldErrors = Object.entries(
    errors?.customFields ?? {}
  ).filter(([fieldId]) => !prunedFieldIds.includes(fieldId));

  return {
    customFields:
      retainedEntries.length > 0
        ? Object.fromEntries(retainedEntries)
        : undefined,
    errors: {
      ...errors,
      customFields:
        retainedCustomFieldErrors.length > 0
          ? Object.fromEntries(retainedCustomFieldErrors)
          : undefined,
    },
  };
};

const normalizeInitialState = (initialState: State): State => {
  const {
    area,
    areaKey,
    category,
    categoryKey,
    department,
    departmentKey,
    ...rest
  } = initialState as State & {
    area?: Area;
    department?: Department;
    category?: Category;
  };

  return {
    ...rest,
    areaKey: areaKey ?? area?.key,
    categoryKey: categoryKey ?? category?.key,
    departmentKey: departmentKey ?? department?.key,
  };
};

export class DraftIssueStore {
  createSlice = (initialState: State = {}) => {
    const normalizedInitialState = normalizeInitialState(initialState);

    const state: Lens<DraftIssueSlice> = (set, get, api) => ({
      ensureSubmissionId: () => {
        const existingId = get().submissionId;

        if (existingId) {
          return existingId;
        }

        const nextId = generateSubmissionId();
        set({ submissionId: nextId });
        return nextId;
      },
      addAttachment: (attachment) =>
        set((draftIssue) => {
          const attachments = draftIssue.attachments
            ? [...draftIssue.attachments, attachment]
            : [attachment];
          return { ...draftIssue, attachments };
        }),
      removeAttachment: (attachment) =>
        set((draftIssue) => {
          const attachments = (draftIssue.attachments || []).filter(
            ({ filename }) => filename !== attachment.filename
          );
          return { ...draftIssue, attachments };
        }),
      removeAttachments: () =>
        set((draftIssue) => {
          return { ...draftIssue, attachments: [] };
        }),
      clearSummaryFields: () =>
        set((draftIssue) => {
          const filteredErrors = Object.fromEntries(
            Object.entries(draftIssue.errors || {}).filter(([key]) => {
              return !SUMMARY_ERROR_KEYS.has(key as keyof SchemaErrors);
            })
          );

          const normalizedErrors =
            Object.keys(filteredErrors).length > 0 ? filteredErrors : undefined;

          return {
            ...draftIssue,
            description: undefined,
            attachments: undefined,
            customFields: undefined,
            hasAcknowledgedNudge: undefined,
            errors: normalizedErrors,
          };
        }),
      clearEscalationData: () =>
        set((draftIssue) => ({
          ...draftIssue,
          escalationData: undefined,
        })),
      reset: () => {
        set(() => ({
          submissionId: undefined,
          areaKey: undefined,
          attachments: undefined,
          categoryKey: undefined,
          departmentKey: undefined,
          description: undefined,
          fields: undefined,
          customFields: undefined,
          errors: undefined,
          escalationData: undefined,
          hasAcknowledgedNudge: undefined,
        }));
      },
      setArea: (area) =>
        set((draftIssue) => {
          const catalogFields = getCatalogFields(api);
          const fields = getIssueFields({ area }, catalogFields);

          return {
            ...draftIssue,
            categoryKey: undefined,
            areaKey: area.key,
            departmentKey: undefined,
            fields,
            ...reconcileCustomFields(draftIssue, fields, catalogFields),
          };
        }),
      setAttachments: (attachments) => set({ attachments }),
      setCategory: (category) =>
        set((draftIssue) => {
          const catalogFields = getCatalogFields(api);
          const fields = getIssueFields({ category }, catalogFields);
          const mergedFields = getMergedCatalogFieldsForSelection(
            catalogFields,
            undefined,
            category
          );

          return {
            ...draftIssue,
            categoryKey: category.key,
            fields,
            ...reconcileCustomFields(draftIssue, fields, mergedFields),
          };
        }),
      setDepartment: (department) =>
        set((draftIssue) => {
          const catalogFields = getCatalogFields(api);
          const fields = getIssueFields({ department }, catalogFields);
          const mergedFields = getMergedCatalogFieldsForSelection(
            catalogFields,
            department,
            undefined
          );

          return {
            ...draftIssue,
            categoryKey: undefined,
            departmentKey: department.key,
            fields,
            ...reconcileCustomFields(draftIssue, fields, mergedFields),
          };
        }),
      setDescription: (description) =>
        set((draftIssue) => {
          const errors = validateField(
            'description',
            description,
            getCatalogFields(api)
          );
          return {
            ...draftIssue,
            description,
            errors: { ...draftIssue.errors, description: errors },
          };
        }),
      setEscalationData: (data) =>
        set((draftIssue) => ({
          ...draftIssue,
          escalationData: {
            ...draftIssue.escalationData,
            ...data,
          },
        })),
      setCustomField: (fieldId, value) =>
        set((draftIssue) => {
          const fields = getSelectionFields(draftIssue, api);
          const fieldErrors = validateCustomField(fieldId, value, fields);
          const existingCustomFieldErrors =
            draftIssue.errors?.customFields ?? {};
          const newCustomFieldErrors: Record<string, string[]> = fieldErrors
            ? { ...existingCustomFieldErrors, [fieldId]: fieldErrors }
            : Object.fromEntries(
                Object.entries(existingCustomFieldErrors).filter(
                  ([key]) => key !== fieldId
                )
              );

          return {
            ...draftIssue,
            customFields: {
              ...draftIssue.customFields,
              [fieldId]: value,
            },
            errors: {
              ...draftIssue.errors,
              customFields:
                Object.keys(newCustomFieldErrors).length > 0
                  ? newCustomFieldErrors
                  : undefined,
            },
          };
        }),
      setHasAcknowledgedNudge: (hasAcknowledgedNudge) =>
        set({ hasAcknowledgedNudge }),
      validateAll: () => {
        const draftIssue = get();
        const result = validateDraftIssueWithZod({
          ...draftIssue,
          ...resolveDraftIssueSelections(draftIssue, getCatalogAreas(api)),
        });

        if (!result.isValid) {
          const errors = result.errors;

          set({ errors });
          return { isValid: false, errors };
        }
        set({ errors: undefined });
        return { isValid: true };
      },
      ...normalizedInitialState,
    });
    return state;
  };

  partialize = ({ draftIssue }: { draftIssue: DraftIssueSlice }) => {
    return {
      ...draftIssue,
    };
  };
}
